/**
 * MSI / Plessey image processor.
 *
 * Turns a camera frame (ImageData) into an array of Scanlines that the
 * decoder can chew on. The pipeline is intentionally simple — no
 * localization, no perspective correction, no fancy heuristics. We
 * just sweep horizontal scan lines across the image, threshold each
 * row, and run-length encode it.
 *
 * Why so simple: MSI shelf tags are usually photographed close-up and
 * head-on. The hard image-processing problems (1D localization in a
 * cluttered scene, perspective deskew, low-contrast tags) belong to a
 * more sophisticated localizer that we can layer on later. For an
 * MVP that field-tests well against real shelf tags, "scan every 12th
 * row" is enough.
 */

import type { GrayImage, Scanline } from './types';

export interface ScanlineExtractionOptions {
  /**
   * Take a scan line every N rows. Lower = more candidates, more
   * compute. 12 is a good default for 1280-wide camera frames (yields
   * ~50–90 scanlines).
   */
  rowStride?: number;

  /**
   * Minimum total run-length count in a row for it to be considered.
   * Rows with only a handful of runs are blank or out-of-focus.
   */
  minRuns?: number;

  /**
   * Maximum total run-length count. Rows with thousands of runs are
   * noisy textures (clothing, foliage) — skip them.
   */
  maxRuns?: number;

  /**
   * Manual threshold override (0..255). When omitted, Otsu's method
   * is used per-row. Useful for tests where you generate clean
   * black/white images and want deterministic threshold = 128.
   */
  threshold?: number;
}

const DEFAULTS: Required<ScanlineExtractionOptions> = {
  rowStride: 12,
  minRuns: 16,
  maxRuns: 1024,
  threshold: -1, // sentinel — use Otsu
};

/**
 * Convert an ImageData (RGBA) to a GrayImage (luminance only).
 * Uses Rec. 709 weights for perceptual luminance.
 */
export function imageDataToGray(imageData: ImageData): GrayImage {
  const { width, height, data } = imageData;
  const gray = new Uint8ClampedArray(width * height);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    // Rec.709 luma: Y' = 0.2126 R + 0.7152 G + 0.0722 B
    gray[j] = (data[i] * 54 + data[i + 1] * 183 + data[i + 2] * 19) >> 8;
  }
  return { data: gray, width, height };
}

/**
 * Compute an Otsu threshold for a row of grayscale pixels. Returns a
 * value 0..255 such that pixels < threshold are "dark" (bars) and
 * pixels >= threshold are "light" (spaces).
 *
 * Otsu's method maximizes between-class variance. Standard algorithm,
 * single-pass histogram-based. ~50 lines including the histogram step.
 */
export function otsuThreshold(row: Uint8ClampedArray | number[], offset = 0, length = -1): number {
  const n = length < 0 ? row.length - offset : length;
  if (n <= 0) return 128;
  const hist = new Uint32Array(256);
  for (let i = 0; i < n; i++) hist[row[offset + i]]++;
  let sumTotal = 0;
  for (let i = 0; i < 256; i++) sumTotal += i * hist[i];
  let sumB = 0;
  let wB = 0;
  let varMax = -1;
  let threshold = 128;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = n - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB;
    const mF = (sumTotal - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > varMax) {
      varMax = between;
      threshold = t;
    }
  }
  return threshold;
}

/**
 * Run-length encode one row of a grayscale image using the given
 * threshold. Returns the alternating-width array + whether the first
 * run is a bar (dark) or space (light). Skips leading/trailing pure
 * white "quiet zone" runs since they're not informative for MSI
 * decoding.
 */
export function rowToRuns(
  gray: GrayImage,
  y: number,
  threshold: number,
): { widths: number[]; firstIsBar: boolean; xOffset: number } {
  const { data, width } = gray;
  const rowStart = y * width;
  const widths: number[] = [];
  // First run starts with whatever the first pixel is.
  let currentIsBar = data[rowStart] <= threshold;
  const firstIsBar = currentIsBar;
  let runLength = 1;
  for (let x = 1; x < width; x++) {
    const isBar = data[rowStart + x] <= threshold;
    if (isBar === currentIsBar) {
      runLength++;
    } else {
      widths.push(runLength);
      currentIsBar = isBar;
      runLength = 1;
    }
  }
  widths.push(runLength);

  // Trim leading/trailing white-space runs (quiet zone). MSI's left
  // quiet zone is by spec at least 10 modules; rightmost trailing
  // white is also non-data.
  // After trimming, recompute firstIsBar.
  const trimmed = trimQuietZones(widths, firstIsBar);
  return trimmed;
}

function trimQuietZones(
  widths: number[],
  firstIsBar: boolean,
): { widths: number[]; firstIsBar: boolean; xOffset: number } {
  let start = 0;
  let end = widths.length;
  let firstBar = firstIsBar;
  let xOffset = 0;
  // Trim leading: if widths[0] is a SPACE (light), drop it; that's
  // the left quiet zone. Preserve its pixel width as xOffset (#2205) so
  // the decoder can map element indices back to absolute frame x.
  if (!firstBar && widths.length > 0) {
    start = 1;
    xOffset = widths[0];
    firstBar = true;
  }
  // Trim trailing: drop a final space run.
  if (end > start) {
    const lastIsBar = firstBar ? (end - 1 - start) % 2 === 0 : (end - 1 - start) % 2 === 1;
    if (!lastIsBar) end -= 1;
  }
  return {
    widths: widths.slice(start, end),
    firstIsBar: firstBar,
    xOffset,
  };
}

/**
 * Sweep the image and extract candidate scanlines. Each scanline is
 * one horizontal row's run-length encoding, ready for the decoder.
 */
export function extractScanlines(
  gray: GrayImage,
  options: ScanlineExtractionOptions = {},
): Scanline[] {
  const opts = { ...DEFAULTS, ...options };
  const stride = Math.max(1, opts.rowStride);
  const out: Scanline[] = [];
  for (let y = 0; y < gray.height; y += stride) {
    const rowStart = y * gray.width;
    const rowSlice = gray.data.subarray(rowStart, rowStart + gray.width);
    const threshold = opts.threshold >= 0 ? opts.threshold : otsuThreshold(gray.data, rowStart, gray.width);
    const runs = rowToRuns(gray, y, threshold);
    if (runs.widths.length >= opts.minRuns && runs.widths.length <= opts.maxRuns) {
      out.push({
        widths: runs.widths,
        firstIsBar: runs.firstIsBar,
        y,
        xOffset: runs.xOffset,
        frameWidth: gray.width,
      });
    }
    // suppress unused row reference (defensive — some bundlers warn)
    void rowSlice;
  }
  return out;
}

/**
 * Build ONE denoised "consensus" scanline by vertically projecting the
 * barcode band (#2209). On marginal real shelf tags each row's run widths are
 * noisy and the per-row decode mis-segments differently (leading drop on some,
 * trailing over-read on others), so no single scanline reads the true code. The
 * per-row noise is largely uncorrelated, so a per-column majority vote across
 * all barcode-like rows reinforces the true bar edges and cancels the noise —
 * yielding cleaner widths than any single row.
 *
 * Returns null when there aren't enough barcode-like rows to project (also the
 * graceful-degradation path for skewed/rotated tags, where column projection
 * would smear — callers fall back to the per-scanline vote).
 */
export function buildConsensusScanline(
  gray: GrayImage,
  options: ScanlineExtractionOptions = {},
): Scanline | null {
  const opts = { ...DEFAULTS, ...options };
  const stride = Math.max(1, opts.rowStride);
  const { width, height } = gray;
  if (width === 0) return null;
  const darkCount = new Float32Array(width);
  let bandRows = 0;
  let ySum = 0;
  for (let y = 0; y < height; y += stride) {
    const rowStart = y * width;
    const threshold = opts.threshold >= 0 ? opts.threshold : otsuThreshold(gray.data, rowStart, width);
    // Count transitions to decide if this row is barcode-like (same gate as
    // extractScanlines: minRuns ≤ runs ≤ maxRuns).
    let transitions = 0;
    let prevDark = gray.data[rowStart] <= threshold;
    for (let x = 1; x < width; x++) {
      const dark = gray.data[rowStart + x] <= threshold;
      if (dark !== prevDark) transitions += 1;
      prevDark = dark;
    }
    if (transitions + 1 < opts.minRuns || transitions + 1 > opts.maxRuns) continue;
    for (let x = 0; x < width; x++) {
      if (gray.data[rowStart + x] <= threshold) darkCount[x] += 1;
    }
    bandRows += 1;
    ySum += y;
  }
  if (bandRows < 3) return null;
  // Per-column majority → consensus binary row (0 = dark/bar, 255 = light).
  const consensus = new Uint8ClampedArray(width);
  const half = bandRows / 2;
  for (let x = 0; x < width; x++) consensus[x] = darkCount[x] >= half ? 0 : 255;
  const runs = rowToRuns({ data: consensus, width, height: 1 }, 0, 128);
  if (runs.widths.length < opts.minRuns) return null;
  return {
    widths: runs.widths,
    firstIsBar: runs.firstIsBar,
    y: Math.round(ySum / bandRows),
    xOffset: runs.xOffset,
    frameWidth: width,
  };
}

/**
 * Convenience: convert ImageData straight to a list of Scanlines.
 * The most common entry point from a camera frame.
 */
export function imageDataToScanlines(
  imageData: ImageData,
  options: ScanlineExtractionOptions = {},
): Scanline[] {
  const gray = imageDataToGray(imageData);
  return extractScanlines(gray, options);
}
